import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowDownRight, ArrowUpRight, Search, X } from "lucide-react";
import {
  ASSET_CLASS_BY_ID,
  ASSET_CLASSES_CATALOG,
} from "@/constants/asset-classes";
import { Layout } from "@/components/Layout";
import { useI18n } from "@/i18n/use-i18n";
import { getIntlLocale } from "@/i18n/locale";
import {
  searchAssetsService,
  type SearchAssetsResult,
} from "@/services";
import "./market.css";
import { AssetClassCode, AssetsSearchResult } from "@/types/assets";

type ClassFilter = AssetClassCode | "all";

const PAGE_SIZE = 24;
const emptyValue = "--";

const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
};

export default function Market() {
  const { locale, strings } = useI18n();
  const intlLocale = getIntlLocale(locale);
  const [query, setQuery] = useState("");
  const [selectedClass, setSelectedClass] = useState<ClassFilter>("all");
  const [page, setPage] = useState(1);
  const [allAssets, setAllAssets] = useState<AssetsSearchResult[]>([]);
  const prevFilterKey = useRef(`${query}|${selectedClass}`);

  const debouncedQuery = useDebouncedValue(query, 350);
  const normalizedQuery = debouncedQuery.trim();

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(intlLocale, {
        style: "currency",
        currency: "BRL",
        maximumFractionDigits: 2,
      }),
    [intlLocale]
  );

  const percentFormatter = useMemo(
    () =>
      new Intl.NumberFormat(intlLocale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [intlLocale]
  );

  // Reset to page 1 when query or class changes
  useEffect(() => {
    const key = `${normalizedQuery}|${selectedClass}`;
    if (key !== prevFilterKey.current) {
      prevFilterKey.current = key;
      setPage(1);
      setAllAssets([]);
    }
  }, [normalizedQuery, selectedClass]);

  const assetsQuery = useQuery<SearchAssetsResult>({
    queryKey: ["search-assets", normalizedQuery, selectedClass, page],
    queryFn: () =>
      searchAssetsService.search({
        query: normalizedQuery || undefined,
        page,
        pageSize: PAGE_SIZE,
        assetClass: selectedClass === "all" ? undefined : selectedClass,
      }),
  });

  // Accumulate pages
  useEffect(() => {
    if (!assetsQuery.data) return;
    const incoming = assetsQuery.data.items ?? [];
    setAllAssets(prev =>
      page === 1 ? incoming : [...prev, ...incoming]
    );
  }, [assetsQuery.data, page]);

  const total = assetsQuery.data?.pagination?.total;
  const hasNext = assetsQuery.data?.pagination?.hasNext ?? false;

  const formatChange = (value: number) => {
    const sign = value > 0 ? "+" : "";
    return `${sign}${percentFormatter.format(value)}%`;
  };

  const isLoadingResults =
    assetsQuery.isLoading || (assetsQuery.isFetching && allAssets.length === 0);
  const isLoadingMore = assetsQuery.isFetching && allAssets.length > 0;

  const queryErrorMessage = assetsQuery.isError
    ? assetsQuery.error instanceof Error
      ? assetsQuery.error.message
      : strings.market.list.error
    : "";

  const classFilters: { value: ClassFilter; label: string; color?: string }[] = [
    { value: "all", label: "Todas" },
    ...ASSET_CLASSES_CATALOG.map((c) => ({
      value: c.id as ClassFilter,
      label: strings.assetClassesCatalog[c.id]?.name ?? c.name,
      color: c.color,
    })),
  ];

  return (
    <Layout>
      <section className="market app-page">
        <header className="market__header">
          <div className="market__titles">
            <h1>{strings.market.header.title}</h1>
            <p className="market__subtitle">Explore e pesquise ativos disponíveis no mercado</p>
          </div>
        </header>

        <div className="market__panel">
          <div className="market__controls">
            <div className="market__search-field">
              <Search size={16} className="market__search-icon" />
              <input
                id="market-search"
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={strings.market.search.placeholder}
              />
              {query ? (
                <button
                  type="button"
                  className="market__clear"
                  onClick={() => setQuery("")}
                  aria-label={strings.market.search.clear}
                >
                  <X size={14} />
                </button>
              ) : null}
            </div>

            <div className="market__pills" role="group" aria-label="Filtrar por classe">
              {classFilters.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  className={`market__pill ${selectedClass === f.value ? "market__pill--active" : ""}`}
                  style={
                    selectedClass === f.value && f.color
                      ? ({ "--pill-color": f.color } as CSSProperties)
                      : undefined
                  }
                  onClick={() => setSelectedClass(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {total !== undefined && !isLoadingResults && (
            <div className="market__count">
              <strong>{total.toLocaleString("pt-BR")}</strong> ativos encontrados
            </div>
          )}

          <div className="market__list" role="list">
            {isLoadingResults ? (
              Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="market__row market__row--skeleton">
                  <div className="market__skeleton-left">
                    <div className="market__skel market__skel--avatar" />
                    <div className="market__skel-info">
                      <div className="market__skel market__skel--title" />
                      <div className="market__skel market__skel--sub" />
                    </div>
                  </div>
                  <div className="market__skel market__skel--badge" />
                  <div className="market__skeleton-right">
                    <div className="market__skel market__skel--price" />
                    <div className="market__skel market__skel--change" />
                  </div>
                </div>
              ))
            ) : queryErrorMessage ? (
              <div className="app-state app-state--error market__empty">
                {queryErrorMessage}
              </div>
            ) : allAssets.length === 0 ? (
              <div className="app-state market__empty">
                {strings.market.list.resultsEmpty}
              </div>
            ) : (
              allAssets.map((asset: AssetsSearchResult) => {
                const displayName =
                  asset.long_name?.trim() || asset.short_name?.trim();
                const classDefinition = ASSET_CLASS_BY_ID[asset.type];
                const changeValue = asset.regular_market_change_percent;
                const changeTone =
                  changeValue === undefined
                    ? "neutral"
                    : changeValue >= 0
                    ? "positive"
                    : "negative";
                const ChangeIcon =
                  changeValue === undefined
                    ? null
                    : changeValue >= 0
                    ? ArrowUpRight
                    : ArrowDownRight;
                const classColor =
                  classDefinition?.color ?? "var(--color-brand)";
                const assetPath = `/market/${asset.ticker.toLowerCase()}`;

                return (
                  <Link
                    key={asset.ticker}
                    to={assetPath}
                    className="market__row"
                    role="listitem"
                  >
                    <div className="market__asset">
                      <div className="market__ticker-group">
                        {asset.logo_url ? (
                          <img
                            className="market__logo"
                            src={asset.logo_url}
                            alt={asset.ticker}
                            loading="lazy"
                          />
                        ) : (
                          <span
                            className="market__logo market__logo--placeholder"
                            aria-hidden="true"
                          >
                            {asset.ticker.slice(0, 1)}
                          </span>
                        )}
                        <div className="market__ticker-info">
                          <span className="market__ticker">{asset.ticker}</span>
                          <span className="market__asset-name">{displayName}</span>
                        </div>
                      </div>
                    </div>

                    <div className="market__badges">
                      {classDefinition?.name ? (
                        <span
                          className="market__badge market__badge--tone"
                          style={{ "--badge-color": classColor } as CSSProperties}
                        >
                          {classDefinition.name}
                        </span>
                      ) : null}
                    </div>

                    <div className="market__meta">
                      <div className="market__price">
                        {asset.price !== undefined
                          ? currencyFormatter.format(asset.price)
                          : emptyValue}
                      </div>
                      <span className={`market__change market__change--${changeTone}`}>
                        {ChangeIcon ? <ChangeIcon size={13} /> : null}
                        {changeValue !== undefined
                          ? formatChange(changeValue)
                          : emptyValue}
                      </span>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          {hasNext && !isLoadingResults && (
            <div className="market__pagination">
              <button
                type="button"
                className="market__load-more"
                onClick={() => setPage((p) => p + 1)}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? "Carregando…" : "Carregar mais"}
              </button>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
